/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { isEmpty, merge, throttle } from 'lodash';

import {
  isLocalStorageExist,
  isSessionStorageExist
} from 'app/common/utils/StorageUtils';

/**
 * This class allows the create of instances to store and access elements within
 * localStorage as a cache. Instances of this class handle the syncing between the
 * local JS cache and localStorage.
 */
class LocalStorageCache {
  static defaults = {
    disablePersist: false,
    enableLocal: true,
    enableSession: false,
    // the wait time in milliseconds between persisting the cache to localStorage
    throttleWait: 500,
    // the default ttl in milliseconds for elements that are cached, 0 means never expired
    ttl: 0,
    // function allowing a transformation of the key values, useful for ensuring a specific key format
    transformKey: key => key,
    // the ttl in seconds, if set, will be used to set the ttl in milliseconds
    ttlSeconds: undefined
  };

  /**
   * The primary constructor that initially hydrates the cache from localStorage.
   *
   * @param {String} name     the name and key used to store this cache in localStorage
   * @param {Object} options  the object containing the options for this cache
   */
  constructor(name, options = {}) {
    options = merge({}, LocalStorageCache.defaults, options);
    this.name = name;
    this.throttleWait = options.throttleWait;
    this.disablePersist = options.disablePersist;
    this.enableLocal = options.enableLocal;
    this.enableSession = options.enableSession;
    this.transformKey = options.transformKey;
    if (options.ttlSeconds) {
      // converted to milliseconds
      options.ttl = options.ttlSeconds * 1000;
    }

    this.defaultOptions = {
      ttl: options.ttl || 0
    };

    console.assert(
      typeof this.defaultOptions.ttl === 'number',
      `Parameter, ttl, must be of type "number", but instead was found to be type "${typeof this
        .defaultOptions.ttl}".`
    );

    this.hydrate();
    this.persist();
  }

  hydrate = () => {
    try {
      if (this.enableSession && isSessionStorageExist()) {
        const cache = deserializeCache(sessionStorage.getItem(this.name));
        if (!isEmpty(cache)) {
          this.cache = cache;
          return;
        }
      }

      if (this.enableLocal && isLocalStorageExist()) {
        const cache = deserializeCache(localStorage.getItem(this.name));
        if (!isEmpty(cache)) {
          this.cache = cache;
          return;
        }
      }

      this.cache = new Map();
    } catch (e) {
      this.cache = new Map();
    }
  };

  persist = throttle(() => {
    if (this.disablePersist) {
      return;
    }

    const serializedCache = serializeCache(this.cache);

    if (this.enableSession && isSessionStorageExist()) {
      sessionStorage.setItem(this.name, serializedCache);
    }

    if (this.enableLocal && isLocalStorageExist()) {
      localStorage.setItem(this.name, serializedCache);
    }
  }, this.throttleWait);

  put = (key, value, options = { ttl: 0 }) => {
    const ttl = options.ttl || this.defaultOptions.ttl || 0;

    console.assert(
      typeof ttl === 'number',
      `Parameter, ttl, must be of type "number", but instead was found to be of type "${typeof ttl}".`
    );

    this.cache.set(this.transformKey(key), {
      expiry: ttl ? now() + ttl : undefined,
      value
    });

    this.persist();
    return this;
  };

  /**
   * Returns the record for the cache key. If the value has expired, returns undefined.
   *
   * @param key - Key of the cache entry
   *
   * @return {*}
   */
  get = key => {
    if (this.disablePersist) {
      return undefined;
    }

    const [record, didExpire] = this.getWithDidExpire(key, false);
    return didExpire ? undefined : record;
  };

  keys = () => {
    return Array.from(this.cache.keys());
  };

  /**
   * Returns the record even if expired along with a flag for whether the cache
   * did expire.
   *
   * @param key - Key of the cache entry
   * @param [removeExpired=true] - Whether to remove expired records from cache
   *
   * @return {[any, boolean]|[undefined, boolean]|[*, boolean]}
   */
  getWithDidExpire = (key, removeExpired = true) => {
    const record = this.cache.get(this.transformKey(key));

    if (record) {
      // if cache is expired, delete the value
      if (hasExpired(record)) {
        removeExpired && this.delete(this.transformKey(key));
        return [record.value, true];
      }

      return [record.value, false];
    }

    return [undefined, false];
  };

  delete = key => {
    const success = this.cache.delete(this.transformKey(key));
    this.persist();
    return success;
  };

  clear = () => {
    this.cache.clear();
    this.persist();
  };

  /**
   * @see #purge
   * @return {number} the current size of the cache, this may contain expired elements
   */
  size = () => this.cache.size;

  /**
   * Purges this cache of any expired elements. Useful before calling {@link #size}
   * to ensure you get an accurate size.
   */
  purge = () => {
    this.cache.forEach((record, key) => {
      if (hasExpired(record)) {
        this.cache.delete(key);
      }
    });
    this.persist();
  };
}

function serializeCache(deserializedMap) {
  return JSON.stringify(Array.from(deserializedMap.entries()));
}

function deserializeCache(serializedEntries) {
  return new Map(JSON.parse(serializedEntries) || []);
}

/**
 * @param  {Object}  record the record with property expiry
 * @return {Boolean}        whether the record is expired
 */
function hasExpired(record) {
  // be sure to use + operator on expiry as the local storage caching converts it to string
  return record.expiry && record.expiry <= now();
}

/**
 * @return {number} the current time using Date.now
 */
function now() {
  if (!Date.now) {
    return new Date().getTime();
  }

  return Date.now();
}

export default LocalStorageCache;
